import { detectCodec } from "./codecs";

export interface VideoProfile {
  /** Stable label — used for telemetry and to decide when the cap wins over a caller's bitrate. */
  kind: "standard" | "capped";
  constraints: MediaTrackConstraints;
  bitrate: number;
}

/**
 * Why the probe landed on the profile it did.
 */
export type VideoProbeOutcome =
  | "supported"
  | "unsupported"
  | "api_unavailable"
  | "no_codec"
  | "probe_failed";

export interface VideoProbeResult {
  outcome: VideoProbeOutcome;
  /** The contentType the probe asked about; null when the probe never ran. */
  contentType: string | null;
}

export interface ResolvedVideoProfile extends VideoProbeResult {
  profile: VideoProfile;
}

const STANDARD_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  facingMode: "user",
};

// `max` (not just `ideal`) also stops an uncapped high-res webcam from exceeding this ceiling.
const CAPPED_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 640, max: 1280 },
  height: { ideal: 480, max: 720 },
  facingMode: "user",
};

export const STANDARD_VIDEO_PROFILE: VideoProfile = {
  kind: "standard",
  constraints: STANDARD_VIDEO_CONSTRAINTS,
  bitrate: 1500000,
};

export const CAPPED_VIDEO_PROFILE: VideoProfile = {
  kind: "capped",
  constraints: CAPPED_VIDEO_CONSTRAINTS,
  bitrate: 800000,
};

const PROBE_FRAMERATE = 30;

// codec.mimeType names the whole container (e.g. `avc1,mp4a.40.2`) for
// MediaRecorder, but Media Capabilities' video.contentType takes only the
// video codec — the codecs list is always video-codec-first (see codecs.ts).
function toVideoOnlyContentType(mimeType: string): string {
  const [base, codecs] = mimeType.split(";codecs=");
  if (codecs == null) return mimeType;
  return `${base};codecs=${codecs.split(",")[0]}`;
}

/** Asks Media Capabilities whether 720p/1.5Mbps is encodable. Reports why when it can't tell. */
export async function probeStandardVideoProfile(): Promise<VideoProbeResult> {
  const encodingInfo = navigator.mediaCapabilities?.encodingInfo;
  if (encodingInfo == null) {
    return { outcome: "api_unavailable", contentType: null };
  }

  const codec = detectCodec("video");
  if (codec == null) return { outcome: "no_codec", contentType: null };

  const contentType = toVideoOnlyContentType(codec.mimeType);

  try {
    const info = await encodingInfo.call(navigator.mediaCapabilities, {
      type: "record",
      video: {
        contentType,
        width: 1280,
        height: 720,
        bitrate: STANDARD_VIDEO_PROFILE.bitrate,
        framerate: PROBE_FRAMERATE,
      },
    });
    return {
      outcome: info.supported ? "supported" : "unsupported",
      contentType,
    };
  } catch {
    return { outcome: "probe_failed", contentType };
  }
}

/** Picks the standard profile unless the probe confirms it won't encode. */
export async function resolveVideoProfile(): Promise<ResolvedVideoProfile> {
  const result = await probeStandardVideoProfile();
  return {
    ...result,
    profile:
      result.outcome === "unsupported"
        ? CAPPED_VIDEO_PROFILE
        : STANDARD_VIDEO_PROFILE,
  };
}
